import { auth } from '../auth/state.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

class ApiError extends Error {
  constructor(status, body) {
    super(body?.detail || friendlyDefault(status));
    this.status = status;
    this.body = body;
    this.kind = classify(status);
  }
}

function classify(status) {
  if (status === 401) return 'auth';
  if (status === 403) return 'permission';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 413) return 'too_large';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

function friendlyDefault(status) {
  switch (classify(status)) {
    case 'auth':       return 'Session expired — sign in again.';
    case 'permission': return "You don't have permission for that.";
    case 'not_found':  return 'Not found.';
    case 'conflict':   return 'Already exists or conflicts with another item.';
    case 'too_large':  return 'File is too large.';
    case 'validation': return 'Some fields are invalid.';
    case 'server':     return 'Server error — try again in a moment.';
    default:           return `HTTP ${status}`;
  }
}

/**
 * Get a friendly, end-user-facing message for any error thrown by the
 * api client (or generic JS Errors). Prefers backend-provided `detail`
 * but always falls back to a humane default. Use this in toasts/alerts.
 */
export function friendlyMessage(err) {
  if (!err) return 'Something went wrong.';
  if (err instanceof ApiError) {
    return err.body?.detail || friendlyDefault(err.status);
  }
  if (err.name === 'TypeError' && /fetch/i.test(err.message)) {
    return "Can't reach the server. Check your connection.";
  }
  return err.message || 'Something went wrong.';
}

async function request(path, { method = 'GET', body, headers = {}, isForm = false, raw = false } = {}) {
  const finalHeaders = { ...headers };
  const token = auth.getToken();
  if (token) finalHeaders['Authorization'] = `Bearer ${token}`;

  let payload;
  if (body !== undefined) {
    if (isForm) {
      payload = body;
    } else {
      finalHeaders['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }

  const resp = await fetch(`${API_URL}${path}`, { method, headers: finalHeaders, body: payload });

  if (resp.status === 401) {
    auth.clear();
    throw new ApiError(401, { detail: 'Session expired — please sign in again' });
  }

  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({}));
    throw new ApiError(resp.status, errBody);
  }

  if (raw) return resp;
  if (resp.status === 204) return null;
  return resp.json();
}

export const api = {
  get:   (path)               => request(path),
  post:  (path, body, opts)   => request(path, { method: 'POST', body, ...opts }),
  put:   (path, body)         => request(path, { method: 'PUT', body }),
  patch: (path, body)         => request(path, { method: 'PATCH', body }),
  del:   (path)               => request(path, { method: 'DELETE' }),
};

export { ApiError, API_URL };
